import { useCourses } from 'core/hooks/course/useCourses';
import { useGroups } from 'core/hooks/useGroups';
import { useMemo } from 'react';

/**
 * useHasGroupWithAiCourse - Whether the teacher has at least one group that
 * contains an AI-enabled course.
 *
 * Radar and the Analytics panel are scoped to exactly those courses (a group with
 * no AI course, or a license-only course with no group, contributes nothing), so
 * this is the signal for "these sections have something to show". It mirrors how
 * the analytics views build their course set: a course counts only when it is
 * AI-enabled (`has_tangerine_ai === 1`) AND belongs to one of the teacher's groups
 * (`school_group_guid`).
 *
 * Reads the persisted Redux slices (groups + courses), so it resolves synchronously
 * for returning users. Fail-open while either slice is still loading: returns true
 * until we can confirm there is no such course, so a real teacher is never hidden
 * or redirected mid-load.
 */
export default function useHasGroupWithAiCourse(): boolean {
  const { groups } = useGroups();
  const { courses } = useCourses();

  return useMemo(() => {
    // Fail-open while either slice is still loading.
    if (!groups || !courses) return true;

    const groupGuids = new Set((groups.schoolGroups ?? []).map((group: { guid: string }) => group.guid));
    if (groupGuids.size === 0) return false;

    return courses.some((course: { has_tangerine_ai: number; school_group_guid: string }) => course.has_tangerine_ai === 1 && groupGuids.has(course.school_group_guid));
  }, [groups, courses]);
}
